// LeadsAIFleetPanel — the "AI is alive" pane.
//
// Reads /api/leads/ai-fleet-status every 60s. Shows each virtual
// AI's current state (working / listening / idle) so the operator
// can see the system is active even between visible actions.
// Collapsed by default to save room; expand to see full fleet.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

const STATUS_TONE = {
  working:   { color: '#22c55e', label: 'Working',  pulse: true },
  listening: { color: '#0891b2', label: 'Listening',pulse: false },
  idle:      { color: '#94a3b8', label: 'Idle',     pulse: false },
};

export default function LeadsAIFleetPanel() {
  const [state, setState] = React.useState({ loading: true, data: null });
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch('/api/leads/ai-fleet-status', { headers: await bearer() });
        const j = await r.json();
        if (!cancelled && r.ok && j.ok) setState({ loading: false, data: j });
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (state.loading || !state.data) return null;
  const d = state.data;

  return (
    <div style={{
      background: '#0f172a',
      border: '1px solid #1e293b',
      borderRadius: 12,
      padding: 12,
      color: '#e2e8f0',
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
        }}
      >
        <div style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#22d3ee', fontWeight: 800 }}>
          🤖 AI Fleet
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#94a3b8' }}>
          <span><span style={{ color: '#22c55e', fontWeight: 800 }}>{d.summary.working}</span> working</span>
          <span><span style={{ color: '#0891b2', fontWeight: 800 }}>{d.summary.listening}</span> listening</span>
          <span><span style={{ color: '#94a3b8', fontWeight: 800 }}>{d.summary.idle}</span> idle</span>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: '#22d3ee', fontWeight: 700 }}>
          {expanded ? 'Hide' : 'Show'}
        </div>
      </button>

      {expanded && (
        <div style={{
          marginTop: 12,
          display: 'grid', gap: 6,
          gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
        }}>
          {d.fleet.map(f => {
            const t = STATUS_TONE[f.status] || STATUS_TONE.idle;
            return (
              <div key={f.key} style={{
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 8, padding: '8px 10px',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <div style={{ fontSize: 16 }}>{f.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0' }}>{f.name}</div>
                  <div style={{ fontSize: 9, color: '#94a3b8' }}>{f.meta}</div>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 9, color: t.color, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: 999, background: t.color,
                    animation: t.pulse ? 'nova-pulse 1.4s ease-in-out infinite' : 'none',
                  }}/>
                  {t.label}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes nova-pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}
